import { LoadingEl, useWebSocket } from "nate-react-api-helpers";
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "../api/API";
import { FeedList, feedSubscribePath, Subscriptions } from "../api/Feed";
import { Project, ProjectFilter, ProjectPalette } from "../api/Projects";
import { Task, TaskFilter } from "../api/Tasks";
import { setProjectPalette } from "./projectColors";
import { useSyncedRef } from "./SyncedRef";

type Feed = {
    tasks: Task[];
    projects: Project[];

    // total is how many tasks the filter matches, which is more than the list holds
    // when only a page of it was asked for
    total: number;
    error: string | null;
}

// the socket opens with the palette, then answers each subscription with the key it
// was asked under
type FeedMessage = FeedList | { palette: ProjectPalette };

type Kind = "task" | "project";

type Sub = {
    kind: Kind;
    filter: TaskFilter | ProjectFilter;
    count: number;
}

type Palette = {
    ready: boolean;
    error: string | null;
}

type Registry = {
    // subscribe returns the function that drops the subscription again
    subscribe: (key: string, kind: Kind, filter: TaskFilter | ProjectFilter) => () => void;
    refresh: () => void;
    feeds: Record<string, Feed>;
    palette: Palette;
}

const FeedContext = createContext<Registry>({
    subscribe: () => () => {},
    refresh: () => {},
    feeds: {},
    palette: { ready: false, error: null },
});

function toFeed(list: FeedList): Feed {
    return {
        tasks: list.tasks || [],
        projects: list.projects || [],
        total: list.total || 0,
        error: list.error || null,
    };
}

function toSubscriptions(entries: [string, Sub][]): Subscriptions {
    const forKind = (kind: Kind) => entries
        .filter(([, sub]) => sub.kind === kind)
        .map(([key, sub]) => ({ key: key, filter: sub.filter }));

    return {
        subscriptions: forKind("task") as Subscriptions["subscriptions"],
        projectSubscriptions: forKind("project") as Subscriptions["projectSubscriptions"],
    };
}

// FeedProvider holds the one socket every live list is served over. The server sends
// a list when it's subscribed to and again on every change in the tenant, so a save
// doesn't have to re-fetch and no page sits on another user's stale data.
//
// A list asked for before the socket is open is read over plain http instead, so a
// page has everything it opens with after one request rather than after the socket
// has connected and answered.
export function FeedProvider(props: PropsWithChildren<{}>) {
    const [feeds, setFeeds] = useState<Record<string, Feed>>({});
    const [palette, setPalette] = useState<Palette>({ ready: false, error: null });

    // one entry per distinct filter, counted so two lists asking for the same thing
    // share a subscription
    const subs = useRef(new Map<string, Sub>());

    // the keys read over http, so a list isn't asked for twice while the socket is
    // still connecting
    const read = useRef(new Set<string>());

    // bumped whenever the set of subscriptions changes, which is what sends it
    const [version, setVersion] = useState(0);

    const onMessage = useCallback((data: string) => {
        const message = JSON.parse(data) as FeedMessage;

        if ("palette" in message) {
            setProjectPalette(message.palette);
            setPalette({ ready: true, error: null });
            return;
        }

        setFeeds(old => ({ ...old, [message.key]: toFeed(message) }));
    }, []);

    const socket = useWebSocket(feedSubscribePath, onMessage, []);

    // the server takes the whole set at once, and needs it again after a reconnect
    useEffect(() => {
        if (!socket) return;
        socket.send(JSON.stringify(toSubscriptions(Array.from(subs.current.entries()))));
    }, [socket, version]);

    useEffect(() => {
        if (socket) return;

        const pending = Array.from(subs.current.entries()).filter(([key]) => !read.current.has(key));
        if (pending.length === 0) return;

        pending.forEach(([key]) => read.current.add(key));

        api.feed.read(toSubscriptions(pending)).then(result => {
            setProjectPalette(result.palette);
            setPalette({ ready: true, error: null });

            setFeeds(old => {
                const next = { ...old };

                for (const list of result.lists) {
                    // a list the socket has answered in the meantime is the fresher
                    // read, and one the page has moved off isn't wanted any more
                    if (next[list.key] || !subs.current.has(list.key)) continue;
                    next[list.key] = toFeed(list);
                }

                return next;
            });
        }).catch(err => {
            const error = err instanceof Error ? err.message : "Unable to load";

            // the keys are let go so a retry asks for them again
            pending.forEach(([key]) => read.current.delete(key));

            setPalette(old => old.ready ? old : { ready: false, error: error });
            setFeeds(old => {
                const next = { ...old };

                for (const [key] of pending) {
                    if (next[key] || !subs.current.has(key)) continue;
                    next[key] = { tasks: [], projects: [], total: 0, error: error };
                }

                return next;
            });
        });
    }, [socket, version]);

    // readAll reads every list on screen over http, for while the socket can't answer
    const readAll = useRef(() => {});
    readAll.current = () => {
        const entries = Array.from(subs.current.entries());
        if (entries.length === 0) return;

        api.feed.read(toSubscriptions(entries)).then(result => {
            setProjectPalette(result.palette);
            setPalette({ ready: true, error: null });

            setFeeds(old => {
                const next = { ...old };

                // a list the page has moved off isn't wanted any more
                for (const list of result.lists) {
                    if (subs.current.has(list.key)) next[list.key] = toFeed(list);
                }

                return next;
            });
        }).catch(err => {
            // the socket brings the lists once it's back, so a failed read here
            // leaves what's on screen rather than replacing it with an error
            console.error(err);
        });
    };

    // a phone drops the socket while the screen is off or the app is in the background,
    // and it takes a while to come back. Coming back to the page reads every list at
    // once, so what's on screen isn't stale in the meantime.
    useEffect(() => {
        const wake = () => {
            if (!document.hidden) readAll.current();
        };

        document.addEventListener("visibilitychange", wake);
        window.addEventListener("focus", wake);
        window.addEventListener("online", wake);

        return () => {
            document.removeEventListener("visibilitychange", wake);
            window.removeEventListener("focus", wake);
            window.removeEventListener("online", wake);
        };
    }, []);

    // without the socket the lists get no updates, and a lost one can take half a
    // minute to reconnect (see useWebSocket). Until it's up, the lists are read over
    // http on a short interval while the page is on screen.
    useEffect(() => {
        if (socket) return;

        const timer = window.setInterval(() => {
            if (!document.hidden) readAll.current();
        }, pollWhileDown);

        return () => window.clearInterval(timer);
    }, [socket]);

    const subscribe = useCallback((key: string, kind: Kind, filter: TaskFilter | ProjectFilter) => {
        const existing = subs.current.get(key);
        if (existing) {
            existing.count++;
        } else {
            subs.current.set(key, { kind: kind, filter: filter, count: 1 });
            setVersion(v => v + 1);
        }

        return () => {
            const sub = subs.current.get(key);
            if (!sub) return;

            sub.count--;
            if (sub.count > 0) return;

            subs.current.delete(key);
            read.current.delete(key);
            setFeeds(old => {
                const next = { ...old };
                delete next[key];
                return next;
            });

            setVersion(v => v + 1);
        };
    }, []);

    // a list that failed goes back to loading, and is asked for again over whichever
    // of the socket and http is there to answer it
    const refresh = useCallback(() => {
        setPalette(old => old.ready ? old : { ready: false, error: null });
        setFeeds(old => {
            const next = { ...old };
            for (const key of Object.keys(next)) {
                if (next[key].error) delete next[key];
            }
            return next;
        });
        setVersion(v => v + 1);
    }, []);

    return (
        <FeedContext.Provider value={{ subscribe: subscribe, refresh: refresh, feeds: feeds, palette: palette }}>
            {props.children}
        </FeedContext.Provider>
    );
}

// pollWhileDown is how often the lists are read over http while the socket is down
const pollWhileDown = 5 * 1000;

const noTasks: Task[] = [];
const noProjects: Project[] = [];

// useFeed watches one list. A change pushed for the filter being watched swaps the
// list in place, so nothing flashes or jumps; only a change of filter goes back to
// loading.
//
// A null filter watches nothing, which is how a list that hasn't been asked for yet
// costs neither a query nor a place in the subscription set.
function useFeed(kind: Kind, filter: TaskFilter | ProjectFilter | null) {
    const registry = useContext(FeedContext);

    // the callbacks are stable, so they can be depended on without re-subscribing on
    // every render the way the registry itself would
    const subscribe = registry.subscribe;
    const refresh = registry.refresh;

    // the kind leads the key so a task filter and a project filter that serialise
    // the same way don't share an entry
    const key = filter ? kind + ":" + JSON.stringify(filter) : "";
    const filterRef = useSyncedRef(filter);

    useEffect(() => {
        if (!key) return;
        return subscribe(key, kind, filterRef.current as TaskFilter | ProjectFilter);
    }, [subscribe, kind, key, filterRef]);

    const feed = key ? registry.feeds[key] : undefined;
    const loading = !!key && !feed;
    const error = feed?.error || null;

    return {
        feed: feed,
        loading: loading,
        error: error,
        LoadingOrErrorElement: LoadingEl(loading, error, refresh),
    };
}

export function useTaskFeed(filter: TaskFilter | null) {
    const result = useFeed("task", filter);
    return { ...result, tasks: result.feed?.tasks || noTasks, total: result.feed?.total || 0 };
}

export function useProjectFeed(filter: ProjectFilter) {
    const result = useFeed("project", filter);
    return { ...result, projects: result.feed?.projects || noProjects };
}

// usePalette says whether the palette is in hand yet (see PaletteGate)
export function usePalette() {
    const registry = useContext(FeedContext);
    return { ...registry.palette, refresh: registry.refresh };
}
